import { isPlaceholderEmail } from "@/lib/person-email";
import { FormattedMessage, useIntl } from "react-intl";
import { ImportPersonAction, ImportPersonWarningCode } from "@/graphql/generated/types";
import type { ImportPersonActionFieldsFragment } from "@/graphql/generated/operations";
import {
  actionsThatRestoreDeletedPerson,
  actionsWithIdentityMismatch,
} from "./import-job-snapshots";

/**
 * The per-Person actions Genesis returns for validation and committed imports.
 *
 * Everything shown here is the backend's own answer. Spark does no matching, no
 * diffing, no name/e-mail heuristic and no duplicate resolution: it renders
 * `action`, the identities genesis read, and the warnings genesis attached. That
 * boundary is the point of the feature, not an implementation shortcut.
 */

const ACTION_LABELS = {
  [ImportPersonAction.Create]: {
    id: "service.page.bulkYaml.personCreate",
    defaultMessage: "Create",
  },
  [ImportPersonAction.Update]: {
    id: "service.page.bulkYaml.personUpdate",
    defaultMessage: "Update",
  },
  [ImportPersonAction.Restore]: {
    id: "service.page.bulkYaml.personRestore",
    defaultMessage: "Restore",
  },
  [ImportPersonAction.Delete]: {
    id: "service.page.bulkYaml.personDelete",
    defaultMessage: "Delete",
  },
} as const;

function IdentityLine({
  label,
  name,
  email,
  showMissingEmail = false,
}: {
  label: React.ReactNode;
  name?: string | null;
  email?: string | null;
  showMissingEmail?: boolean;
}) {
  if (!name && !email && !showMissingEmail) return null;
  return (
    <div className="flex flex-wrap items-baseline gap-x-2">
      <span className="w-16 shrink-0 text-smycles-label text-ink-muted">{label}</span>
      {name ? <span className="text-smycles-meta text-ink">{name}</span> : null}
      {email || showMissingEmail ? (
        <span className="text-smycles-meta text-ink-muted">
          {email && !isPlaceholderEmail(email) ? email : (
            <FormattedMessage id="person.email.missing" defaultMessage="No email address provided" />
          )}
        </span>
      ) : null}
    </div>
  );
}

function PersonActionRow({
  action,
  index,
  intl,
  dryRun,
}: {
  action: ImportPersonActionFieldsFragment;
  index: number;
  intl: ReturnType<typeof useIntl>;
  dryRun: boolean;
}) {
  const mismatch = action.warnings.includes(ImportPersonWarningCode.PersonIdentityMismatch);
  const isRestore = action.action === ImportPersonAction.Restore;
  const actionLabel = intl.formatMessage(ACTION_LABELS[action.action]);
  const isCreate = action.action === ImportPersonAction.Create;

  return (
    <li
      role="group"
      aria-label={intl.formatMessage(
        {
          id: "service.page.bulkYaml.personRowLabel",
          defaultMessage: "Person {n}: {action}",
        },
        { n: index + 1, action: actionLabel },
      )}
      className={`rounded-md p-3 ring-1 ${
        mismatch || isRestore ? "bg-attention/[0.07] ring-attention/30" : "bg-ink/[0.02] ring-ink/10"
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded bg-ink/[0.06] px-1.5 py-0.5 text-smycles-micro font-medium text-ink">
          {actionLabel}
        </span>
        {action.personId ? (
          <span className="font-mono text-smycles-micro text-ink-muted">{action.personId}</span>
        ) : isCreate ? (
          // Never invent a UUID here. Genesis reports generated database ids on
          // the corresponding unit result, not on this document-action record.
          <span className="text-smycles-label text-ink-muted">
            <FormattedMessage
              id={dryRun
                ? "service.page.bulkYaml.personIdGenerated"
                : "service.page.bulkYaml.personIdWasGenerated"}
              defaultMessage={dryRun
                ? "The UUID is generated during the import."
                : "The UUID was generated during the import."}
            />
          </span>
        ) : null}
      </div>

      <div className="mt-1.5 flex flex-col gap-0.5">
        {!isRestore ? (
          <IdentityLine
            label={<FormattedMessage id="service.page.bulkYaml.personExisting" defaultMessage="Existing" />}
            name={action.existingDisplayName}
            email={action.existingPrimaryEmail}
            showMissingEmail={!isCreate}
          />
        ) : null}
        <IdentityLine
          label={<FormattedMessage id="service.page.bulkYaml.personIncoming" defaultMessage="Import" />}
          name={action.incomingDisplayName}
          email={action.incomingPrimaryEmail}
          showMissingEmail={isCreate}
        />
      </div>

      {isRestore ? (
        <div className="mt-2 border-t border-attention/20 pt-2">
          <div className="text-smycles-meta font-medium text-ink-attention">
            <FormattedMessage
              id={dryRun
                ? "service.page.bulkYaml.personRestoreValidationTitle"
                : "service.page.bulkYaml.personRestoreResultTitle"}
              defaultMessage={dryRun
                ? "Deleted profile will be restored"
                : "Deleted profile was restored"}
            />
          </div>
          <p className="mt-0.5 text-smycles-label text-ink">
            <FormattedMessage
              id={dryRun
                ? "service.page.bulkYaml.personRestoreValidationText"
                : "service.page.bulkYaml.personRestoreResultText"}
              defaultMessage={dryRun
                ? "This UUID belongs to a deleted person. The import restores the profile data contained in the YAML. Data not contained in the YAML will not be restored."
                : "The profile was restored with the data contained in the YAML. Data not contained in the YAML was not restored."}
            />
          </p>
        </div>
      ) : null}

      {mismatch ? (
        <div className="mt-2 border-t border-attention/20 pt-2">
          <div className="text-smycles-meta font-medium text-ink-attention">
            <FormattedMessage
              id="service.page.bulkYaml.personMismatchTitle"
              defaultMessage="Possibly wrong person ID"
            />
          </div>
          <p className="mt-0.5 text-smycles-label text-ink">
            <FormattedMessage
              id="service.page.bulkYaml.personMismatchText"
              defaultMessage="Name and primary e-mail both differ from the stored person. The import updates the existing person with this UUID."
            />
          </p>
        </div>
      ) : null}
    </li>
  );
}

export function ImportPersonActions({
  personActions,
  dryRun,
}: {
  personActions: readonly ImportPersonActionFieldsFragment[];
  dryRun: boolean;
}) {
  const intl = useIntl();
  if (personActions.length === 0) return null;
  const mismatches = actionsWithIdentityMismatch(personActions);
  const restorations = actionsThatRestoreDeletedPerson(personActions);
  // A plain statement, deliberately not a checkbox: genesis restores and
  // updates by UUID regardless of any client-side tick (a direct import never
  // even shows this preview), so offering a choice here would promise a
  // control that does not exist. The honest lever is named instead: fix the
  // file.
  const noticeRequired = dryRun && (mismatches.length > 0 || restorations.length > 0);

  return (
    <section className="mt-3">
      <h4 className="smycles-field-meta font-medium text-ink">
        <FormattedMessage id="service.page.bulkYaml.personActionsTitle" defaultMessage="Person changes" />
      </h4>
      <ul className="mt-2 flex flex-col gap-1.5">
        {personActions.map((action, index) => (
          <PersonActionRow
            key={`${action.documentPosition}-${action.personId ?? index}`}
            action={action}
            index={index}
            intl={intl}
            dryRun={dryRun}
          />
        ))}
      </ul>

      {noticeRequired ? (
        <p className="mt-3 rounded-md bg-attention/[0.07] p-3 text-smycles-meta text-ink ring-1 ring-attention/30">
          {restorations.length > 0 && mismatches.length > 0 ? (
            <FormattedMessage
              id="service.page.bulkYaml.personRiskNotice"
              defaultMessage="Running the import applies the shown updates and restores the deleted profiles. If that is not intended, correct the UUIDs in the file first."
            />
          ) : restorations.length > 0 ? (
            <FormattedMessage
              id="service.page.bulkYaml.personRestoreNotice"
              defaultMessage="{n, plural, one {Running the import restores this deleted profile. If that is not intended, remove the UUID from the file first.} other {Running the import restores these deleted profiles. If that is not intended, remove the UUIDs from the file first.}}"
              values={{ n: restorations.length }}
            />
          ) : (
            <FormattedMessage
              id="service.page.bulkYaml.personMismatchNotice"
              defaultMessage="{n, plural, one {Running the import updates the existing person with this UUID. If that is not intended, correct the UUID in the file first.} other {Running the import updates the existing persons with these UUIDs. If that is not intended, correct the UUIDs in the file first.}}"
              values={{ n: mismatches.length }}
            />
          )}
        </p>
      ) : null}
    </section>
  );
}
